import { useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { EditNote, EditOutlined } from '@mui/icons-material';
import {
  setAttendeeGroupPreferenceNote,
  useActor,
} from '../../lib/audited-writes';
import { useToast } from '../../lib/toast-context';
import { GROUP_PREFERENCE_NOTE_MAX_LENGTH } from '../../lib/constants';
import type { Attendee } from '../../lib/types';

// Pencil icon + popover for editing an attendee's `group_preference_note`.
// Available to any enabled user — notes are operational context (e.g.
// "talked to mom, prefers this") that volunteers may need to capture
// during check-in, so we don't gate edit access to admins. The note text
// is also surfaced in the Person icon's tooltip alongside this editor.
//
// Icon swaps based on whether a note already exists:
//   - `EditNote`     — has a note (filled glyph)
//   - `EditOutlined` — no note yet (outlined glyph)
//
// The popover holds a multiline TextField, a char counter, and three
// buttons:
//   - Save    — disabled when the draft equals the saved value (no-op)
//   - Cancel  — discards the draft, closes the popover
//   - Delete  — only visible when a note exists; clears it via the
//               `setAttendeeGroupPreferenceNote(.., null)` deleteField sentinel
//
// Closing via clickaway / Escape discards the draft (no implicit save).
// Errors surface through `useToast` to match the rest of the app.
const GroupPreferenceNoteEditor = ({ attendee }: { attendee: Attendee }) => {
  const actor = useActor();
  const toast = useToast();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  if (!attendee.group_preference) return null;

  const saved = attendee.group_preference_note ?? '';
  const hasNote = saved.length > 0;
  const trimmedDraft = draft.trim();
  const dirty = trimmedDraft !== saved.trim();
  const overLimit = draft.length > GROUP_PREFERENCE_NOTE_MAX_LENGTH;

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setDraft(saved);
    setOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const writeNote = async (value: string | null) => {
    setSaving(true);
    try {
      await setAttendeeGroupPreferenceNote(actor, attendee, value);
      setOpen(false);
    } catch (err) {
      toast.error(
        `Could not update preference note: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!dirty || overLimit) return;
    // Trimmed empty → clear. Otherwise write the (helper trims again).
    void writeNote(trimmedDraft || null);
  };

  const handleDelete = () => {
    void writeNote(null);
  };

  const fullName = `${attendee.first_name} ${attendee.last_name}`.trim();
  const Icon = hasNote ? EditNote : EditOutlined;
  const tooltipTitle = hasNote
    ? 'Edit preference note'
    : 'Add preference note';

  return (
    <>
      <Tooltip title={tooltipTitle} placement='top'>
        <IconButton
          ref={anchorRef}
          size='small'
          onClick={handleOpen}
          aria-label={`${tooltipTitle} for ${fullName}`}
          sx={{ p: 0.25, displayPrint: 'none' }}
        >
          <Icon fontSize='small' sx={{ fontSize: '1.1rem' }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 360, maxWidth: '90vw' } } }}
      >
        <Stack spacing={1.5}>
          <Typography variant='subtitle2'>
            Preference note · {fullName}
          </Typography>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            multiline
            minRows={3}
            maxRows={8}
            autoFocus
            fullWidth
            size='small'
            placeholder='Add context for this preference (visible to all volunteers)'
            error={overLimit}
            helperText={`${draft.length}/${GROUP_PREFERENCE_NOTE_MAX_LENGTH}`}
            inputProps={{ 'aria-label': 'Preference note' }}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
          >
            {hasNote ? (
              <Button
                size='small'
                color='error'
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </Button>
            ) : (
              <Box />
            )}
            <Stack direction='row' spacing={1}>
              <Button size='small' onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                size='small'
                variant='contained'
                onClick={handleSave}
                disabled={!dirty || overLimit || saving}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default GroupPreferenceNoteEditor;
